import React from 'react'
import PropTypes, { object } from 'prop-types'
import classnames from 'classnames'
import { useState, useEffect } from 'react'

import { useTranslation } from 'react-i18next'
import MediaDropzone from './elements/MediaDropzone'
import MediaList from './elements/MediaList/MediaList.component'

import { actions } from './media'
import PortalModal from 'components/Shared/PortalModal'

import { MediaExpandedModal } from './elements/MediaExpandedModal'
import { useMediaUploadService } from './MediaUpload.service'

const MediaUpload = ({
  initialMedia,
  onChange,
  onClearValidationErrors,
  readonly,
  canAdd,
  canRemove,
  maxItems = Number.MAX_VALUE,
  isCampaignRunning,
}) => {

  const { 
    media, 
    canAddInternal, 
    reachedMaxLimit, 
    isMobileSize, 
    expandedImage, 
    isFirstItem,
    isLastItem,
    ref, 
    state, 
    dispatch, 
    onNext, 
    onBack, 
    removeFile, 
    removeGroup, 
    removeFromGroup, 
    handleAddFileToGroup, 
    handleReplaceFileInGroup, 
    getRootProps, 
    getInputProps, 
    open, 
    setExpandedImage 
  } = useMediaUploadService({ initialMedia, onChange, canAdd, maxItems })

  const [noDelete, setNoDelete] = useState(null)
  const { t } = useTranslation()

  useEffect(() => {
    if (noDelete) {
      onClearValidationErrors?.()
    }
  }, [noDelete, onClearValidationErrors])

  const mediaUploadsClasses = classnames({
    'media-uploads': true,
    'media-uploads-empty': !media.length,
    'mobile-view': isMobileSize,
    'justify-content-between': media.length,
    'justify-content-center': !media.length,
    readonly: readonly,
  })

  return (
    <div>
      {noDelete && (
        <p className="warning-red mb-2">
          {t('Recommendations.campaign.setup.media.errors.noDelete', {
            mediaType: noDelete.mediaType,
            mediaTypeToRemove: noDelete.mediaTypeToRemove,
          })}
        </p>
      )}

      <div ref={ref} className={mediaUploadsClasses}>
        {!readonly && (
          <MediaDropzone
            media={media}
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isMobileSize={isMobileSize}
            canAddInternal={canAddInternal}
            isCampaignRunning={isCampaignRunning}
          />
        )}

        <MediaList
          onRemove={(fileId) => removeFile(fileId)}
          onRemoveGroup={(groupId) => removeGroup(groupId)}
          onRemoveFromGroup={(fileId) => removeFromGroup(fileId)}
          media={media}
          fileToReplace={state.fileToReplace}
          readonly={readonly}
          canAdd={canAdd}
          canAddInternal={canAddInternal}
          reachedMaxLimit={reachedMaxLimit}
          canRemove={canRemove}
          onRetry={(file) => dispatch({ type: actions.retryUploadAsync, payload: file })}
          onReplaceStarted={(index) => {
            dispatch({ type: actions.startReplace, payload: index })
            open()
          }}
          isMobileSize={isMobileSize}
          setExpandedImage={setExpandedImage}
          onAddFileToGroup={handleAddFileToGroup}
          onReplaceFileInGroup={handleReplaceFileInGroup}
          onValidationError={(errorMessages) => {
            onChange(media, null, false, errorMessages, false, false)
          }}
          setNoDelete={setNoDelete}
        />
      </div>
      
      {!!state.error && <p className="warning-red mt-2 text-left">{state.error}</p>}

      {expandedImage && (
        <PortalModal
          isOpen={!!expandedImage}
          className="full-height"
          onClickOutside={() => setExpandedImage(null)}
          title='Media Preview'
        >
          <MediaExpandedModal
            expandedImage={expandedImage}
            setExpandedImage={setExpandedImage}
            onBack={onBack}
            onNext={onNext}
            isFirstItem={isFirstItem}
            isLastItem={isLastItem}
          />
        </PortalModal>
      )}
    </div>
  )
}

MediaUpload.propTypes = {
  initialMedia: PropTypes.arrayOf(object),
  onChange: PropTypes.func.isRequired,
  onClearValidationErrors: PropTypes.func,
  maxItems: PropTypes.number,
  readonly: PropTypes.bool,
  canAdd: PropTypes.bool,
  canRemove: PropTypes.bool,
  validationError: PropTypes.bool,
  isCampaignRunning: PropTypes.bool,
}

export default MediaUpload
